import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CulturalHeritage = () => {
  const [activeTab, setActiveTab] = useState('traditions');

  const traditions = [
  {
    id: 1,
    title: "SUBARAW Biodiversity Festival: Where Culture Meets Conservation",
    category: "Environmental Awareness Through Arts",
    image: "/assets/images/SUBARAW_Biodiversity_Festival-1769668385604.png",
    imageAlt: "Community celebration at SUBARAW Biodiversity Festival showing people performing with marine-themed decorative streamers and costumes celebrating PPSRNP\'s marine biodiversity through traditional Filipino dance and cultural performance",
    description: "Experience the vibrant spirit of Puerto Princesa Subterranean River National Park through the spectacular SUBARAW Biodiversity Festival—a community cultural performance that brings environmental awareness to life through traditional Filipino arts. During this annual celebration, local performers transform conservation messages into captivating dance, music, and visual storytelling that honors both cultural heritage and natural treasures. These dynamic presentations, featuring choreographed movements inspired by marine life and underwater ecosystems, demonstrate how PPSRNP's conservation success is deeply rooted in community pride and participation. Every graceful gesture and colorful costume tells the story of the park's incredible biodiversity, from coral reefs to forest canopies, while celebrating the resilient communities who serve as guardians of this UNESCO World Heritage Site.",
    practices: [
    "Traditional dance performances celebrating marine biodiversity",
    "SUBARAW Biodiversity Festival annual cultural programming",
    "Environmental storytelling through choreographed arts",
    "Community engagement through performance arts"],
    keeper: "PPSRNP Community Cultural Groups",
    status: "Active annual celebrations engaging thousands of participants"
  },
  {
    id: 2,
    title: "Tourism Development Updates: Nurturing Tomorrow\'s Conservation Heroes",
    category: "Youth Environmental Education",
    image: "/assets/images/Tourism_Development_Updates-1769668491901.JPG",
    imageAlt: "Children in traditional blue and yellow cultural costumes at community celebration showcasing cultural heritage and conservation awareness at PPSRNP",
    description: "Meet the bright faces of Puerto Princesa Subterranean River National Park's future—local children proudly wearing traditional Filipino cultural costumes during community celebrations that weave together environmental stewardship and cultural heritage. These young environmental ambassadors represent the heart of PPSRNP's conservation success: a community that passes love for nature and cultural pride from generation to generation. During festivals like Araw ng Taraw, children learn that protecting their natural heritage is inseparable from honoring their cultural identity, creating lifelong guardians who understand that conservation is not just policy, but a way of life. Their joyful participation in traditional dances, storytelling, and environmental education programs ensures that PPSRNP's legacy will flourish for decades to come.",
    practices: [
    "Youth participation in Araw ng Taraw celebrations",
    "Traditional costume cultural education programs",
    "Intergenerational conservation knowledge transfer",
    "Environmental awareness through cultural activities"],
    keeper: "PPSRNP Youth Education Programs",
    status: "Ongoing youth engagement with 500+ children participating annually"
  },
  {
    id: 3,
    title: "Medicinal Plant Knowledge",
    category: "Traditional Medicine",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14a9d6fc1-1765058947558.png",
    imageAlt: "Traditional healer woman surrounded by array of medicinal plants and herbs in bamboo house with mortar and pestle preparing natural remedies using ancestral knowledge",
    description: "Indigenous healers maintain comprehensive knowledge of over 200 medicinal plants found in PPSRNP. This pharmacological wisdom, passed through generations, includes preparation methods, dosage protocols, and sustainable harvesting techniques that ensure plant population viability.",
    practices: [
    "Identification of 200+ medicinal species",
    "Preparation methods for different ailments",
    "Sustainable harvesting protocols",
    "Spiritual rituals accompanying plant collection"],

    keeper: "Lola Maria Santos, Traditional Healer",
    status: "Knowledge being documented and preserved"
  },
  {
    id: 4,
    title: "Sustainable Fishing Traditions",
    category: "Marine Resource Management",
    image: "https://images.unsplash.com/photo-1683796510962-8b2c10b80ecd",
    imageAlt: "Filipino fisherman in traditional wooden outrigger boat demonstrating ancestral fishing techniques using handwoven nets in crystal clear coastal waters with coral reefs visible below",
    description: "Coastal communities have developed sophisticated fishing practices that maintain marine ecosystem health. Traditional methods include lunar cycle-based fishing calendars, gear restrictions, and community-enforced marine protected areas established long before modern conservation science.",
    practices: [
    "Bubo - Traditional fish traps allowing juvenile escape",
    "Panghuli - Lunar calendar-based fishing schedules",
    "Sagip-dagat - Community marine sanctuary system",
    "Bawal - Seasonal fishing restrictions for spawning periods"],

    keeper: "Community Fishing Cooperative",
    status: "Integrated with modern fisheries management"
  }];


  const culturalSites = [
  {
    id: 1,
    name: "Tagbanua Ancestral Domain",
    type: "Sacred Territory",
    significance: "15,000 hectares of ancestral land with documented occupation spanning 500+ years",
    image: "https://images.unsplash.com/photo-1694460102833-5dd764b74080",
    imageAlt: "Panoramic view of pristine ancestral domain showing dense tropical forest covering rolling hills with traditional Tagbanua settlement visible in valley and sacred mountain peaks in distance",
    features: ["Sacred burial grounds", "Traditional hunting territories", "Medicinal plant gardens", "Ceremonial sites"],
    access: "Guided tours with community permission"
  },
  {
    id: 2,
    name: "Underground River Cultural Zone",
    type: "Spiritual Site",
    significance: "Sacred cave system central to Tagbanua creation mythology and spiritual practices",
    image: "https://images.unsplash.com/photo-1604741529501-b7cf849fd29a",
    imageAlt: "Mystical limestone cave entrance with traditional offerings and ceremonial items placed by indigenous community members with natural light filtering through opening creating spiritual atmosphere",
    features: ["Creation mythology sites", "Ritual offering locations", "Ancient rock art", "Spiritual cleansing pools"],
    access: "Cultural tours with indigenous guides"
  },
  {
    id: 3,
    name: "Traditional Craft Village",
    type: "Living Heritage Center",
    significance: "Active demonstration of traditional crafts, architecture, and daily life practices",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19c0b8d9e-1765151405426.png",
    imageAlt: "Traditional Filipino village with authentic bamboo and nipa palm houses showing artisans demonstrating weaving basket making and other ancestral crafts with visitors observing cultural practices",
    features: ["Traditional house construction", "Weaving demonstrations", "Pottery making", "Traditional cooking methods"],
    access: "Open to visitors with cultural orientation"
  }];


  const culturalPrograms = [
  {
    id: 1,
    title: "Indigenous Youth Mentorship",
    description: "Connecting elders with young people to ensure traditional knowledge transmission",
    participants: 45,
    duration: "Ongoing",
    impact: "15 youth certified as traditional knowledge apprentices"
  },
  {
    id: 2,
    title: "Cultural Immersion Tours",
    description: "Authentic cultural experiences led by community members, generating sustainable income",
    participants: 1200,
    duration: "Year-round",
    impact: "₱2.4M annual income for 25 families"
  },
  {
    id: 3,
    title: "Traditional Arts Revival",
    description: "Workshops reviving endangered crafts like traditional weaving and pottery",
    participants: 78,
    duration: "6-month cycles",
    impact: "12 endangered craft techniques preserved"
  }];


  return (
    <section className="py-16 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            Cultural Heritage & Traditional Knowledge
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
            Preserving indigenous wisdom and cultural practices that have sustained this ecosystem for generations.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => setActiveTab('traditions')}
            className={`px-6 py-3 rounded-lg font-medium transition-all border border-[var(--color-border)] ${
            activeTab === 'traditions' ? 'bg-primary text-white shadow-conservation' : 'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/50'}`
            }>

            Traditional Knowledge
          </button>
          <button
            onClick={() => setActiveTab('sites')}
            className={`px-6 py-3 rounded-lg font-medium transition-all border border-[var(--color-border)] ${
            activeTab === 'sites' ? 'bg-primary text-white shadow-conservation' : 'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/50'}`
            }>

            Cultural Sites
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            className={`px-6 py-3 rounded-lg font-medium transition-all border border-[var(--color-border)] ${
            activeTab === 'programs' ? 'bg-primary text-white shadow-conservation' : 'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/50'}`
            }>

            Cultural Programs
          </button>
        </div>

        {/* Traditional Practices Tab */}
        {activeTab === 'traditions' &&
        <div className="space-y-8">
            {traditions?.map((tradition) =>
          <div
            key={tradition?.id}
            className="bg-background rounded-lg overflow-hidden shadow-conservation-lg">

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Image */}
                  <div className="relative h-[400px] overflow-hidden">
                    <Image
                  src={tradition?.image}
                  alt={tradition?.imageAlt}
                  className="w-full h-full object-cover" />

                    <div className="absolute top-4 left-4 bg-accent text-[var(--color-foreground)] px-4 py-2 rounded-full text-sm font-medium">
                      {tradition?.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 lg:p-12">
                    <h3 className="text-2xl font-bold text-white mb-4">{tradition?.title}</h3>
                    <p className="text-gray-300 mb-6 leading-relaxed">{tradition?.description}</p>

                    {/* Practices */}
                    <div className="mb-6">
                      <h4 className="text-lg font-bold text-white mb-3">Key Practices</h4>
                      <ul className="space-y-2">
                        {tradition?.practices?.map((practice, index) =>
                    <li key={index} className="flex items-start gap-2 text-gray-300">
                            <Icon name="Leaf" size={16} className="text-success mt-1 flex-shrink-0" />
                            <span>{practice}</span>
                          </li>
                    )}
                      </ul>
                    </div>

                    {/* Keeper Info */}
                    <div className="flex items-start gap-3 p-4 bg-muted rounded-lg mb-4">
                      <Icon name="User" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-accent mb-1">Knowledge Keeper</div>
                        <div className="text-sm text-gray-300">{tradition?.keeper}</div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex items-center gap-2 text-sm text-success">
                      <Icon name="CheckCircle2" size={16} />
                      <span>{tradition?.status}</span>
                    </div>
                  </div>
                </div>
              </div>
          )}
          </div>
        }

        {/* Cultural Sites Tab */}
        {activeTab === 'sites' &&
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {culturalSites?.map((site) =>
          <div
            key={site?.id}
            className="bg-background rounded-lg overflow-hidden shadow-conservation hover:shadow-conservation-lg transition-all animation-organic group">

                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <Image
                src={site?.image}
                alt={site?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                  <div className="absolute top-4 right-4 bg-accent text-[var(--color-foreground)] px-3 py-1 rounded-full text-sm font-medium">
                    {site?.type}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-accent transition-colors">
                    {site?.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">{site?.significance}</p>

                  {/* Features */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white mb-2">Key Features</h4>
                    <div className="flex flex-wrap gap-2">
                      {site?.features?.map((feature, index) =>
                  <span
                    key={index}
                    className="px-3 py-1 bg-muted text-gray-300 rounded-full text-xs">

                          {feature}
                        </span>
                  )}
                    </div>
                  </div>

                  {/* Access */}
                  <div className="flex items-center gap-2 text-sm text-info pt-4 border-t border-border">
                    <Icon name="Info" size={16} />
                    <span>{site?.access}</span>
                  </div>
                </div>
              </div>
          )}
          </div>
        }

        {/* Cultural Programs Tab */}
        {activeTab === 'programs' &&
        <div className="space-y-6">
            {culturalPrograms?.map((program) =>
          <div
            key={program?.id}
            className="bg-background rounded-lg p-8 shadow-conservation hover:shadow-conservation-lg transition-all animation-organic">

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3">{program?.title}</h3>
                    <p className="text-gray-300 mb-4">{program?.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Icon name="Users" size={16} />
                        <span>{program?.participants} participants</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Icon name="Clock" size={16} />
                        <span>{program?.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="lg:w-80">
                    <div className="bg-success/10 border border-success rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Icon name="TrendingUp" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-success mb-1">Impact</div>
                          <div className="text-sm text-gray-300">{program?.impact}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          )}

            {/* Call to Action */}
            <div className="bg-gradient-conservation rounded-lg p-8 text-center mt-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Experience Living Culture
              </h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Join our cultural immersion programs and learn directly from indigenous knowledge keepers. All proceeds support community development and cultural preservation.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button variant="default" iconName="Calendar" iconPosition="left">
                  Book Cultural Tour
                </Button>
                <Button variant="outline">
                  Support Cultural Programs
                </Button>
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

};

export default CulturalHeritage;